import React, { useState, useEffect } from 'react';
import { formatearFecha, formatearMonto } from '../../api/aplicacionPagos';
import { enviarReciboCargosManuales, getCiclosEscolares } from '../../api/cargosManuales';
import { generateReciboPDF } from '../../utils/generateReciboPDF';
import styles from '../../styles/AplicacionPagosStyles/AplicacionPagos.module.css';

/**
 * Enriquece el objeto estudiante con programa_nombre y beca_activa_nombre
 * para asegurar que el recibo tenga todos los datos necesarios
 */
function enriquecerEstudianteParaRecibo(studentData) {
  const studentEnriquecido = { ...studentData };
  
  // Asegurar que programa_nombre esté disponible
  if (!studentEnriquecido.programa_nombre) {
    // Intentar obtener de id_programa_actual si es un objeto
    if (studentEnriquecido.id_programa_actual && typeof studentEnriquecido.id_programa_actual === 'object') {
      studentEnriquecido.programa_nombre = studentEnriquecido.id_programa_actual.nombre_programa || '';
    } else {
      studentEnriquecido.programa_nombre = '';
    }
  }
  
  // Asegurar que beca_activa_nombre esté disponible
  if (!studentEnriquecido.beca_activa_nombre) {
    studentEnriquecido.beca_activa_nombre = '';
  }
  
  // Asegurar que seccion esté disponible
  if (!studentEnriquecido.seccion) {
    // Intentar obtener de id_seccion si es un objeto
    if (studentEnriquecido.id_seccion && typeof studentEnriquecido.id_seccion === 'object') {
      studentEnriquecido.seccion = studentEnriquecido.id_seccion.nombre_seccion || '';
    } else {
      studentEnriquecido.seccion = '';
    }
  }
  
  return studentEnriquecido;
}

function PaymentDetail({ student, onBack, onRegisterPayment, onBackToCargosManuales, campus, loading }) {
  const [paymentData, setPaymentData] = useState({
    forma_pago: '',
    referencia_bancaria: '',
    monto_recibido: '',
    fecha_pago: new Date().toISOString().split('T')[0],
    observaciones: ''
  });
  
  const [selectedCargos, setSelectedCargos] = useState([]);
  const [ciclos, setCiclos] = useState([]);
  const [cicloSeleccionado, setCicloSeleccionado] = useState('');

  // Cargar ciclos para determinar el periodo actual según la fecha
  useEffect(() => {
    const loadCiclos = async () => {
      try {
        // Primero intentamos obtener solo el ciclo vigente (por fecha_actual entre fecha_inicio y fecha_fin)
        let response = await getCiclosEscolares({ vigente: true });

        // Si no hay ciclos vigentes (por configuración de fechas), caemos a todos los activos
        if (!response || response.length === 0) {
          response = await getCiclosEscolares();
        }

        setCiclos(response || []);

        if (response && response.length > 0) {
          let cicloInicial = null;
          const cicloEstudiante =
            student.ciclo ||
            student.ciclo_escolar ||
            student.periodo_actual ||
            student.periodoActual;

          if (cicloEstudiante) {
            cicloInicial = response.find(
              (c) =>
                c.ciclo === cicloEstudiante ||
                c.nombre === cicloEstudiante ||
                `${c.anio}-${c.numero_periodo}` === cicloEstudiante
            );
          }

          if (!cicloInicial) {
            cicloInicial = response[0];
          }

          if (cicloInicial) {
            setCicloSeleccionado(cicloInicial.id_ciclo_escolar);
          }
        }
      } catch (err) {
        console.error('Error al cargar ciclos escolares para periodo actual en pagos:', err);
      }
    };

    loadCiclos();
  }, [student]);

  const handleInputChange = (field, value) => {
    setPaymentData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCargoSelection = (cargoId, monto) => {
    const exists = selectedCargos.find(c => c.id_cargo === cargoId);
    
    if (exists) {
      // Deseleccionar
      setSelectedCargos(selectedCargos.filter(c => c.id_cargo !== cargoId));
    } else {
      // Seleccionar
      setSelectedCargos([...selectedCargos, {
        id_cargo: cargoId,
        monto_aplicado: monto
      }]);
    }
  };

  const handleMontoAplicadoChange = (cargoId, nuevoMonto, saldoPendiente) => {
    const montoNumerico = parseFloat(nuevoMonto) || 0;
    // No permitir que el monto exceda el saldo pendiente
    const montoValido = Math.min(montoNumerico, parseFloat(saldoPendiente));
    
    setSelectedCargos(selectedCargos.map(c => 
      c.id_cargo === cargoId 
        ? { ...c, monto_aplicado: montoValido }
        : c
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedCargos.length === 0) {
      alert('Debe seleccionar al menos un concepto para aplicar el pago');
      return;
    }
    
    // Validar que todos los montos sean mayores a cero
    const montosInvalidos = selectedCargos.filter(c => c.monto_aplicado <= 0);
    if (montosInvalidos.length > 0) {
      alert('Todos los montos a aplicar deben ser mayores a cero');
      return;
    }
    
    // Validar que el monto recibido sea suficiente
    const totalAplicar = selectedCargos.reduce((sum, c) => sum + c.monto_aplicado, 0);
    const montoRecibido = parseFloat(paymentData.monto_recibido) || 0;
    
    if (montoRecibido < totalAplicar) {
      alert(`El monto recibido ($${montoRecibido.toFixed(2)}) es menor que el total a aplicar ($${totalAplicar.toFixed(2)})`);
      return;
    }

    const effectiveCampusId = student.id_campus || campus;

    const pagoCompleto = {
      id_estudiante: student.id,
      id_campus: effectiveCampusId,
      forma_pago: paymentData.forma_pago,
      referencia_bancaria: paymentData.referencia_bancaria,
      monto_recibido: montoRecibido,
      fecha_pago: paymentData.fecha_pago,
      hora_pago: new Date().toTimeString().split(' ')[0],
      observaciones: paymentData.observaciones,
      aplicaciones: selectedCargos
    };

    try {
      const response = await onRegisterPayment(pagoCompleto);

      if (!response || !response.pago) {
        alert('El servidor no devolvió la información del pago.');
        return;
      }

      // Construir lista de cargos para el PDF a partir de los adeudos seleccionados
      let cargosParaPDF = [];
      if (Array.isArray(selectedCargos) && selectedCargos.length > 0 && Array.isArray(student.adeudos)) {
        cargosParaPDF = selectedCargos.map((sel) => {
          const adeudo = student.adeudos.find((a) => a.id === sel.id_cargo) || {};
          return {
            id_cargo: sel.id_cargo,
            monto: sel.monto_aplicado || adeudo.saldo_pendiente || 0,
            monto_pagado: sel.monto_aplicado || adeudo.saldo_pendiente || 0,
            cantidad: 1,
            motivo: adeudo.concepto || adeudo.descripcion || '',
            id_concepto: adeudo.codigo_concepto || adeudo.codigo || '',
            codigo_concepto: adeudo.codigo_concepto || '',
            concepto: adeudo.concepto || adeudo.nombre_concepto || '',
            subcategoria: adeudo.subcategoria || '',
            descripcion_concepto: adeudo.descripcion_concepto || '',
            // Incluir descuento y recargo del cargo
            descuento_beca: adeudo.descuento_beca_monto || 0,
            monto_recargo: adeudo.monto_recargo || 0,
          };
        });
      }

      // Generar PDF de recibo (si hay cargos)
      if (cargosParaPDF.length > 0) {
        try {
          // Enriquecer datos del estudiante para el recibo
          const studentParaRecibo = enriquecerEstudianteParaRecibo(student);
          
          const dataUri = await generateReciboPDF({
            student: studentParaRecibo,
            charges: cargosParaPDF,
            paymentData: {
              forma_pago: paymentData.forma_pago,
              referencia_bancaria: paymentData.referencia_bancaria,
              observaciones: paymentData.observaciones,
              codigo_recibo: response?.pago?.folio_recibo,
            },
          });

          if (dataUri && student.email) {
            const paymentUrl = response.payment_url || (response.pago && response.pago.payment_url) || null;
            const paymentUrlError = response.payment_url_error || (response.pago && response.pago.payment_url_error) || null;

            if (paymentUrl) {
              console.log('[WEBPAY] Liga de pago generada correctamente:', paymentUrl);
            } else if (paymentUrlError) {
              console.warn('[WEBPAY] No se generó liga de pago:', paymentUrlError);
            } else {
              console.log('[WEBPAY] No se recibió liga de pago (forma de pago distinta de PAGO_EN_LINEA o integración desactivada).');
            }

            const base64 = dataUri.includes(',') ? dataUri.split(',')[1] : dataUri;
            try {
              await enviarReciboCargosManuales({
                toEmail: student.email,
                pdfBase64: base64,
                subject: 'Recibo de pago Universidad Lux',
                paymentUrl,
              });
            } catch (emailError) {
              console.error('Error al enviar el recibo por correo:', emailError);
              // No bloquear por error de correo
            }
          }
        } catch (pdfError) {
          console.error('Error al generar el PDF de recibo:', pdfError);
        }
      }

      // Limpiar selección y formulario
      setSelectedCargos([]);
      setPaymentData({
        forma_pago: '',
        referencia_bancaria: '',
        monto_recibido: '',
        fecha_pago: new Date().toISOString().split('T')[0],
        observaciones: '',
      });

      const folio = response.pago.folio_recibo;

      if (onBackToCargosManuales) {
        const confirmar = window.confirm(
          `Pago registrado exitosamente\nFolio: ${folio}\n\n¿Desea regresar a la pantalla de Cargos Manuales?`
        );
        if (confirmar) {
          onBackToCargosManuales();
        } else {
          onBack();
        }
      } else {
        alert(`Pago registrado exitosamente\nFolio: ${folio}`);
        onBack();
      }
    } catch (error) {
      console.error('Error al registrar pago:', error);
      alert(`Error: ${error.message || 'Error al registrar el pago'}`);
    }
  };

  // Calcular totales
  const totalAdeudos = student.adeudos.reduce((sum, adeudo) => sum + parseFloat(adeudo.saldo_pendiente), 0);
  const totalSeleccionado = selectedCargos.reduce((sum, c) => sum + c.monto_aplicado, 0);
  const montoRecibido = parseFloat(paymentData.monto_recibido) || 0;
  const cambio = montoRecibido > totalSeleccionado ? montoRecibido - totalSeleccionado : 0;

  // Ciclo seleccionado para mostrar en "Periodo actual"
  const cicloSeleccionadoObj = ciclos.find(
    (c) =>
      c.id_ciclo_escolar === cicloSeleccionado ||
      c.id_ciclo_escolar === parseInt(cicloSeleccionado, 10)
  );

  return (
    <div className={styles.paymentDetailContainer}>
      {/* Back Buttons */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px' }}>
        <button className={styles.backButton} onClick={onBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M19 12H5"/>
            <path d="M12 19l-7-7 7-7"/>
          </svg>
          Volver a búsqueda
        </button>
        
        {onBackToCargosManuales && (
          <button 
            className={styles.backButton} 
            onClick={onBackToCargosManuales}
            style={{ backgroundColor: '#002f6c', color: '#fff' }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M19 12H5"/>
              <path d="M12 19l-7-7 7-7"/>
            </svg>
            Regresar a Cargos Manuales
          </button>
        )}
      </div>

      {/* Student Info Card */}
      <div className={styles.studentInfoCard}>
        <div className={styles.studentInfoHeader}>
          <span className={styles.studentInfoTitle}>Estudiante: {student.nombre}</span>
        </div>
        
        <div className={styles.studentInfoBody}>
          <div className={styles.studentAvatar}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </div>
          
          <div className={styles.studentInfoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Matrícula</span>
              <span className={styles.infoValue}>{student.matricula}</span>
            </div>
            
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Email institucional</span>
              <span className={styles.infoValue}>{student.email || 'N/A'}</span>
            </div>

            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Periodo actual</span>
              <span className={styles.infoValue}>
                {cicloSeleccionadoObj?.ciclo ||
                  student.ciclo ||
                  student.periodo_actual ||
                  student.periodoActual ||
                  'N/A'}
              </span>
            </div>
            
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Sección</span>
              <span className={styles.infoValue}>{student.seccion}</span>
            </div>
            
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Institución</span>
              <span className={styles.infoValue}>{student.institucion}</span>
            </div>
            
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Programa</span>
              <span className={styles.infoValue}>{student.programa}</span>
            </div>
            
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Campus</span>
              <span className={styles.infoValue}>{student.campus}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Data Form */}
      <form onSubmit={handleSubmit}>
        <div className={styles.paymentDataCard}>
          <div className={styles.paymentDataHeader}>
            <div className={styles.paymentDataIcon}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
              </svg>
            </div>
            <h3 className={styles.paymentDataTitle}>Datos del Pago</h3>
          </div>
          
          <div className={styles.paymentDataBody}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Forma de Pago</label>
                <select 
                  className={styles.formSelect}
                  value={paymentData.forma_pago}
                  onChange={(e) => handleInputChange('forma_pago', e.target.value)}
                  required
                  disabled={loading}
                >
                  <option value="">-- Seleccionar forma de pago --</option>
                  <option value="EFECTIVO">Efectivo</option>
                  <option value="TRANSFERENCIA">Transferencia bancaria</option>
                  <option value="DEPOSITO">Depósito bancario</option>
                </select>
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Referencia Bancaria (Opcional)</label>
                <input
                  type="text"
                  className={styles.formInput}
                  value={paymentData.referencia_bancaria}
                  onChange={(e) => handleInputChange('referencia_bancaria', e.target.value)}
                  placeholder="Número de referencia"
                  disabled={loading}
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Monto recibido</label>
                <input
                  type="number"
                  step="0.01"
                  className={styles.formInput}
                  value={paymentData.monto_recibido}
                  onChange={(e) => handleInputChange('monto_recibido', e.target.value)}
                  placeholder="0.00"
                  required
                  disabled={loading}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Fecha de pago</label>
                <div className={styles.dateInputContainer}>
                  <input
                    type="date"
                    className={styles.dateInput}
                    value={paymentData.fecha_pago}
                    onChange={(e) => handleInputChange('fecha_pago', e.target.value)}
                    required
                    disabled={loading}
                  />
                  <div className={styles.dateIcon}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Observaciones</label>
                <textarea
                  className={styles.formTextarea}
                  value={paymentData.observaciones}
                  onChange={(e) => handleInputChange('observaciones', e.target.value)}
                  rows="3"
                  placeholder="Agregar observaciones..."
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Pending Debts Table */}
        <div className={styles.debtsSection}>
          <div className={styles.debtsSectionHeader}>
            <div className={styles.debtsSectionIcon}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </div>
            <h3 className={styles.debtsSectionTitle}>Adeudos Pendientes del Estudiante (Seleccione los conceptos a pagar)</h3>
          </div>
          
          <div className={styles.debtsTable}>
            <div className={styles.debtsTableHeader}>
              <div className={styles.debtsHeaderCell}>Sel.</div>
              <div className={styles.debtsHeaderCell}>Concepto</div>
              <div className={styles.debtsHeaderCell}>Fecha Vencimiento</div>
              <div className={styles.debtsHeaderCell}>Monto Total</div>
              <div className={styles.debtsHeaderCell}>Pagado</div>
              <div className={styles.debtsHeaderCell}>Saldo Pendiente</div>
              <div className={styles.debtsHeaderCell}>Monto a Aplicar</div>
            </div>
            
            <div className={styles.debtsTableBody}>
              {student.adeudos.map((adeudo) => {
                const isSelected = selectedCargos.find(c => c.id_cargo === adeudo.id);
                const montoAplicar = isSelected ? isSelected.monto_aplicado : 0;
                
                return (
                  <div key={adeudo.id} className={styles.debtsTableRow}>
                    <div className={styles.debtsTableCell}>
                      <input
                        type="checkbox"
                        checked={!!isSelected}
                        onChange={() => handleCargoSelection(adeudo.id, parseFloat(adeudo.saldo_pendiente))}
                        disabled={loading}
                      />
                    </div>
                    <div className={styles.debtsTableCell}>{adeudo.concepto}</div>
                    <div className={styles.debtsTableCell}>{formatearFecha(adeudo.fecha_vencimiento)}</div>
                    <div className={styles.debtsTableCell}>{formatearMonto(adeudo.monto_neto)}</div>
                    <div className={styles.debtsTableCell}>{formatearMonto(adeudo.monto_pagado)}</div>
                    <div className={styles.debtsTableCell}>{formatearMonto(adeudo.saldo_pendiente)}</div>
                    <div className={styles.debtsTableCell}>
                      {isSelected ? (
                        <input
                          type="number"
                          step="0.01"
                          value={montoAplicar}
                          onChange={(e) => handleMontoAplicadoChange(adeudo.id, e.target.value, adeudo.saldo_pendiente)}
                          max={adeudo.saldo_pendiente}
                          min="0"
                          style={{ width: '100px' }}
                          disabled={loading}
                        />
                      ) : (
                        '-'
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className={styles.debtsTableFooter}>
              <span className={styles.totalLabel}>Total a aplicar:</span>
              <span className={styles.totalValue}>{formatearMonto(totalSeleccionado)}</span>
            </div>
          </div>
        </div>

        {/* Payment Summary */}
        <div className={styles.paymentSummary}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Total adeudos:</span>
            <span className={styles.summaryValue}>{formatearMonto(totalAdeudos)}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Monto seleccionado:</span>
            <span className={styles.summaryValue}>{formatearMonto(totalSeleccionado)}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Monto recibido:</span>
            <span className={styles.summaryValue}>{formatearMonto(montoRecibido)}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Cambio:</span>
            <span className={styles.summaryValue}>{formatearMonto(cambio)}</span>
          </div>
        </div>

        {/* Submit Button */}
        <div className={styles.submitSection}>
          <button 
            type="submit" 
            className={styles.submitButton}
            disabled={loading || selectedCargos.length === 0}
          >
            {loading ? 'PROCESANDO...' : 'REGISTRAR PAGO'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PaymentDetail;
